import React, { useState, useEffect } from 'react';
import { UploadCloud, Briefcase, Brain, History, Lightbulb, BarChart2, Info, FileText, X } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api from '../api';
import { getCurrentUserRole, shouldFetchStaffAiConfig } from '../utils/aiAgentsAccess';
import Loader from '../components/Loader';
import Chatbot from '../components/AiAgents/Chatbot';
import ResumeScorerResults from '../components/AiAgents/ResumeScorerResults';
import AvatarModal from '../components/AiAgents/AvatarModal';
import HardwareCheck from '../components/AiAgents/HardwareCheck';
import PerformanceReport from '../components/AiAgents/PerformanceReport';
import StudentLayout from '../components/StudentLayout';
import { useFeatures } from '../components/FeatureGate';
import FeatureUnavailable from '../components/FeatureUnavailable';
import AiDisclaimerCard from '../components/AiDisclaimerCard';
import './AiAgents.css';

const SUGGESTED_TOPICS = [
  "Prompt Engineering",
  "Generative AI & RAG",
  "Quantum Computing",
  "Zero-Knowledge Proofs",
  "Space Exploration",
  "CRISPR Gene Editing",
  "Brain-Computer Interfaces"
];

const AGENT_TAB_TO_MODE = {
  'resume-scorer': 'scorer',
  counsellor: 'counsellor',
  interviewer: 'interviewer',
  tutor: 'tutor',
};

const AGENT_MODE_TO_TAB = {
  scorer: 'resume-scorer',
  counsellor: 'counsellor',
  interviewer: 'interviewer',
  tutor: 'tutor',
};

// Mirrors LEVEL_UI_SUMMARY in backend/services/interview_difficulty.py. Keep the
// three labels in step with the prompt text, or the setup card promises a depth
// the interviewer does not actually apply.
const LEVEL_SUMMARIES = {
  Basic: 'Explain what you built. One question per resume item and one per project - no architecture, scaling or trade-off probing.',
  Intermediate: 'Defend your decisions. Two resume and two project questions, each followed by a "why this choice" probe.',
  Hard: 'Pressure test. Three-deep project ladders (why -> what breaks at 10x -> what you would rewrite) plus JD scenario questions.',
};

const JD_MODES = [
  { key: 'none', label: 'Not Provided' },
  { key: 'text', label: 'Paste Text' },
  { key: 'file', label: 'Upload File' },
];

const MAX_JD_CHARS = 20000;
const JD_FILE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt'];

const parseProjectList = (value) => {
  if (Array.isArray(value)) return value.filter((project) => project && typeof project === 'object');
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed)
        ? parsed.filter((project) => project && typeof project === 'object')
        : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

function AiAgents({ embedded = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { agentTab: routeTab } = useParams();
  const { isSubEnabled } = useFeatures();
  // First enabled agent mode becomes the default (sub-feature aware).
  const defaultMode = isSubEnabled('ai_agents', 'ai_resume_scorer') ? 'scorer'
    : isSubEnabled('ai_agents', 'ai_tutor') ? 'tutor'
    : isSubEnabled('ai_agents', 'ai_counsellor') ? 'counsellor'
    : isSubEnabled('ai_agents', 'ai_interviewer') ? 'interviewer'
    : null;
  // The AI Agents page is only usable when at least one agent mode is enabled
  // for the role.
  const anyAgentEnabled = defaultMode !== null;
  const [formData, setFormData] = useState({
    topic: '',
    resume: null // Used for Tutor mode notes
  });

  const [resumeFile, setResumeFile] = useState(null); // Used for initial upload
  const [hasResume, setHasResume] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);

  const [submitted, setSubmitted] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);
  const pathTab = location.pathname.startsWith('/ai-agents/')
    ? location.pathname.slice('/ai-agents/'.length)
    : '';
  const urlMode = AGENT_TAB_TO_MODE[routeTab || pathTab] || null;
  const [mode, setMode] = useState(() => urlMode || defaultMode);
  const [level, setLevel] = useState('Intermediate');
  // Job Description + GitHub project inputs for the AI Interviewer.
  const [jdInputMode, setJdInputMode] = useState('none');
  const [jdText, setJdText] = useState('');
  const [jdFile, setJdFile] = useState(null);
  const [jdFileName, setJdFileName] = useState('');
  const [jdWarning, setJdWarning] = useState('');
  const [jdDragActive, setJdDragActive] = useState(false);
  const [validatingJd, setValidatingJd] = useState(false);
  const [interviewStep, setInterviewStep] = useState('setup');
  const [showAvatar, setShowAvatar] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessionMeta, setSessionMeta] = useState(null);

  const [beyAgentId, setBeyAgentId] = useState('');
  const [beyEmbedUrl, setBeyEmbedUrl] = useState('');
  const [resolvedEmbedUrl, setResolvedEmbedUrl] = useState('');
  const [agentStatus, setAgentStatus] = useState('unchecked');
  const [scorerResult, setScorerResult] = useState(null);
  const [scorerLocked, setScorerLocked] = useState(false);

  const [latestReport, setLatestReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [showHistoryInitially, setShowHistoryInitially] = useState(false);


  const [aiUsage, setAiUsage] = useState(0);
  const [aiLimit, setAiLimit] = useState(3600);
  const [aiRemaining, setAiRemaining] = useState(null);

  const [jobRoles, setJobRoles] = useState([]);
  const [selectedJobRole, setSelectedJobRole] = useState('');
  const [loadingJobRoles, setLoadingJobRoles] = useState(false);

  // AI Disclaimer gate
  const [showAiDisclaimer, setShowAiDisclaimer] = useState(false);
  const [pendingSubmitEvent, setPendingSubmitEvent] = useState(null);

  // Each agent is its own page: the parent /ai-agents route forwards to the
  // first agent this role can use, and a sub that got disabled falls back the
  // same way instead of rendering an agent the student may not open.
  useEffect(() => {
    if (!anyAgentEnabled) return;
    const modeSub = { scorer: 'ai_resume_scorer', tutor: 'ai_tutor', counsellor: 'ai_counsellor', interviewer: 'ai_interviewer' };
    const modeDisabled = !isSubEnabled('ai_agents', modeSub[mode]);
    const onParentRoute = location.pathname === '/ai-agents';
    if (!modeDisabled && !onParentRoute) return;
    const targetTab = AGENT_MODE_TO_TAB[modeDisabled ? defaultMode : mode];
    const targetPath = targetTab ? `/ai-agents/${targetTab}` : '/ai-agents';
    if (location.pathname !== targetPath) {
      if (modeDisabled) setMode(defaultMode);
      navigate(targetPath, { replace: true });
    }
  }, [mode, isSubEnabled, defaultMode, anyAgentEnabled, location.pathname, navigate]);

  // Deep links: the sidebar submenu and browser back/forward drive `mode`
  // through the URL (/ai-agents/resume-scorer | counsellor | interviewer | tutor).
  useEffect(() => {
    if (!urlMode) return;
    if (urlMode !== mode) setMode(urlMode);
  }, [urlMode, mode]);

  // Load the stored evaluation for the CURRENT resume version. The backend
  // fingerprints the resume content, so this returns a result only when this
  // exact resume has been evaluated before; a modified resume returns none and
  // the Evaluate button becomes available again.
  const refreshScorerAssessment = async () => {
    try {
      const res = await api.get('/ai-agents/resume/latest');
      const assessment = res.data?.assessment;
      if (res.data?.has_assessment && assessment) {
        setScorerResult(assessment);
        setScorerLocked(true);
      } else {
        setScorerResult(null);
        setScorerLocked(false);
      }
    } catch (err) {
      console.warn('Could not load latest resume assessment:', err);
    }
  };

  // Results belong to a single agent, so they must not survive a page switch.
  //
  // For the Resume Scorer we also restore the saved evaluation for the current
  // resume version: a resume that has already been evaluated is locked, so the
  // page must show its stored score instead of offering another AI run.
  useEffect(() => {
    setScorerResult(null);
    setScorerLocked(false);
    setLatestReport(null);
    if (mode === 'scorer') {
      refreshScorerAssessment();
    }
  }, [mode]);

  const [toast, setToast] = useState(null);

  const formatErrorMessage = (err) => {
    if (!err) return '';
    if (typeof err === 'string') return err;
    if (Array.isArray(err)) {
      return err.map(item => formatErrorMessage(item)).filter(Boolean).join('; ');
    }
    if (typeof err === 'object') {
      if (err.msg) return err.msg;
      if (err.detail) return formatErrorMessage(err.detail);
      if (err.message) return formatErrorMessage(err.message);
      try {
        return JSON.stringify(err);
      } catch (e) {
        return 'An error occurred.';
      }
    }
    return String(err);
  };

  const showToast = (message, type = 'error') => {
    const formattedMsg = formatErrorMessage(message);
    setToast({ message: formattedMsg, type });
    setTimeout(() => setToast(null), 5000);
  };

  const fetchLatestReport = async (targetMode = mode) => {
    if (targetMode === 'scorer') {
      setLatestReport(null);
      return;
    }
    setLoadingReport(true);
    try {
      const res = await api.get(`/ai-agents/reports/latest?mode=${targetMode}`);
      if (res.data && res.data.report) {
        setLatestReport(res.data);
      } else {
        setLatestReport(null);
      }
    } catch (e) {
      console.error('Failed to fetch latest report:', e);
    } finally {
      setLoadingReport(false);
    }
  };

  const [studentProfile, setStudentProfile] = useState(null);

  // Projects on the profile. Only a count is shown: the interviewer picks the
  // relevant ones server-side from the job title and JD, so there is nothing
  // here for the student (or a stale client) to narrow or forget to send.
  const availableProjects = React.useMemo(
    () => parseProjectList(studentProfile?.github_projects),
    [studentProfile]
  );

  const handleSessionEnded = (resData) => {
    if (resData) {
      const reportContent = resData.final_index || resData.report || resData;
      setLatestReport({
        sessionId: resData.sessionId || currentSessionId,
        mode: resData.mode || mode,
        createdAt: resData.createdAt || new Date().toISOString(),
        completedAt: resData.completedAt || new Date().toISOString(),
        studentName: resData.studentName || studentProfile?.full_name || studentProfile?.name,
        institutionName: resData.institutionName || studentProfile?.institution_name || studentProfile?.university_name,
        usn: resData.usn || studentProfile?.usn,
        report: reportContent
      });
      showToast(`${mode.charAt(0).toUpperCase() + mode.slice(1)} completed! AI Performance Evaluation report is ready.`, "info");
    } else {
      fetchLatestReport(mode);
    }
    // Take the student straight to the report: it is mounted below the modal, so
    // once the modal closes it only needs to be brought into view.
    window.setTimeout(() => {
      const section = document.getElementById('ai-interviewer-report');
      if (section && section.scrollIntoView) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
  };

  useEffect(() => {
    fetchProfileAndConfig();
  }, []);

  const fetchProfileAndConfig = async () => {
    try {
      // `/ai-agents/config` is staff-only. Students get their embed URLs from
      // the interview/counsellor/tutor start endpoints, so calling this as a
      // student only produced a 403 in the console (and never returned data).
      if (shouldFetchStaffAiConfig(getCurrentUserRole())) {
        try {
          const configRes = await api.get('/ai-agents/config');
          if (configRes.data) {
            setBeyAgentId((configRes.data.beyAgentId || '').trim());
            setBeyEmbedUrl((configRes.data.beyEmbedUrl || '').trim());
            setResolvedEmbedUrl((configRes.data.resolvedEmbedUrl || '').trim());
            setAgentStatus((configRes.data.agentStatus || 'unchecked').trim());
          }
        } catch (cfgErr) {
          // Config restricted or unavailable; use default state
        }
      }

      // Fetch Profile
      const profileRes = await api.get('/student/profile/me');
      if (profileRes.data) {
        setStudentProfile(profileRes.data);
        if (profileRes.data.resume_text) {
          setHasResume(true);
        }
      }

      // Fetch Usage for the header meter (part of the AI Agents feature;
      // not role-gated per-sub).
      try {
        const usageRes = await api.get('/ai-agents/usage');
        setAiUsage(usageRes.data.usage);
        setAiLimit(usageRes.data.limit);
        setAiRemaining(typeof usageRes.data.remaining === 'number' ? usageRes.data.remaining : null);
      } catch (e) {
        console.error("Failed to fetch AI usage", e);
      }

      // Fetch Dynamic Job Roles for Interviewer
      await fetchJobRoles();
    } catch (e) {
      console.error(e);
      if (e.response && e.response.status === 403) {
        showToast("AI Agents is not available for your account.", "error");
        const role = (JSON.parse(localStorage.getItem('user') || '{}').role) || 'student';
        const home = { admin: '/admin-dashboard', system_admin: '/admin-dashboard', university: '/uni-dashboard', student: '/student-dashboard', faculty: '/faculty-dashboard', tpo: '/tpo-dashboard', pop: '/pop-dashboard' }[role] || '/student-dashboard';
        navigate(home);
      }
    } finally {
      setIsLoadingProfile(false);
    }
  };

  const fetchJobRoles = async () => {
    try {
      setLoadingJobRoles(true);
      const rolesRes = await api.get('/ai-agents/job-roles');
      if (rolesRes.data && Array.isArray(rolesRes.data.roles)) {
        setJobRoles(rolesRes.data.roles);
      }
    } catch (e) {
      console.error("Failed to fetch job roles", e);
    } finally {
      setLoadingJobRoles(false);
    }
  };

  // Re-fetch job roles whenever switching to Interviewer mode
  useEffect(() => {
    if (mode === 'interviewer') {
      fetchJobRoles();
    }
  }, [mode]);

  useEffect(() => {
    let interval = null;
    let timeout = null;
    if (showAvatar && aiUsage < aiLimit) {
      // 10 second grace period before starting the usage timer
      timeout = setTimeout(() => {
        interval = setInterval(async () => {
          try {
            const res = await api.post('/ai-agents/usage/heartbeat', { seconds: 10 });
            setAiUsage(res.data.usage);
            if (typeof res.data.remaining === 'number') setAiRemaining(res.data.remaining);
            if (res.data.usage >= res.data.limit) {
              setShowAvatar(false);
              showToast("AI Time Limit Reached. Please request an extension.", "error");
            }
          } catch (e) {
            console.error("Heartbeat failed", e);
          }
        }, 10000);
      }, 10000);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
      if (interval) clearInterval(interval);
    };
  }, [showAvatar, aiUsage, aiLimit]);

  const formatTime = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m}m ${s}s`;
  };

  // Hover copy for the "AI Time Used" badge, derived from the current usage.
  const buildUsageTooltip = () => {
    const limitMinutes = Math.max(0, Math.floor(aiLimit / 60));
    const remainingSeconds = Math.max(0, aiRemaining != null ? aiRemaining : aiLimit - aiUsage);
    const pctUsed = aiLimit > 0 ? Math.min(100, Math.round((aiUsage / aiLimit) * 100)) : 100;
    let note = `You have ${formatTime(remainingSeconds)} of AI Agent time left out of the ${limitMinutes} minutes allocated to you.`;
    if (remainingSeconds <= 0) {
      note = 'You have used your full AI Agent allowance. Ask your university administrator to extend your minutes so you can continue.';
    } else if (pctUsed >= 80) {
      note = `Only ${formatTime(remainingSeconds)} left of your ${limitMinutes}-minute allowance. Request extra minutes from your administrator to keep practising.`;
    }
    return { limitMinutes, remainingSeconds, pctUsed, note };
  };

  const handleInitialResumeUpload = async (e) => {
    e.preventDefault();
    if (!resumeFile) {
      showToast("Please select a PDF file.", "error");
      return;
    }

    setUploadingResume(true);
    try {
      const data = new FormData();
      data.append('resume', resumeFile);

      await api.post('/ai-agents/upload-resume', data);
      setHasResume(true);
      setResumeFile(null);
      // A new upload may be a modified resume: re-check whether this content has
      // already been evaluated so the scorer locks or unlocks accordingly.
      await refreshScorerAssessment();
    } catch (error) {
      console.error("Upload error:", error);
      showToast(error.response?.data?.detail || "Failed to upload resume.", "error");
    } finally {
      setUploadingResume(false);
    }
  };

  const applyJdFile = (file) => {
    if (!file) return;
    const name = String(file.name || '');
    if (!JD_FILE_EXTENSIONS.some((extension) => name.toLowerCase().endsWith(extension))) {
      setJdWarning('Upload the job description as a PDF, DOC, DOCX or TXT file.');
      return;
    }
    setJdFile(file);
    setJdFileName(name);
    setJdWarning('');
  };

  const resetJobDescription = () => {
    setJdText('');
    setJdFile(null);
    setJdFileName('');
    setJdWarning('');
  };

  // Runs BEFORE the hardware check: a job description that does not match the
  // job title must never reach /start, and the reason must stay on screen next
  // to the field the student has to fix.
  const validateJobDescription = async () => {
    if (jdInputMode === 'none') {
      setJdWarning('');
      return true;
    }
    if (jdInputMode === 'text' && !jdText.trim()) {
      setJdWarning('Paste the job description, or switch the Job Description to "Not Provided".');
      return false;
    }
    if (jdInputMode === 'file' && !jdFile) {
      setJdWarning('Choose a job description file, or switch the Job Description to "Not Provided".');
      return false;
    }

    setValidatingJd(true);
    setJdWarning('');
    try {
      const data = new FormData();
      data.append('job_role', selectedJobRole.trim());
      if (jdInputMode === 'file' && jdFile) {
        data.append('job_description_file', jdFile);
      } else {
        data.append('job_description_text', jdText.trim());
      }
      const response = await api.post('/sidebar-interviewer/validate-jd', data);
      if (response.data && response.data.relevant === false) {
        const reason = formatErrorMessage(response.data.reason || '');
        setJdWarning(
          `This job description doesn't match the job title "${selectedJobRole.trim()}".`
          + (reason ? ` ${reason}` : '')
          + ' Fix the job title or upload the matching job description to continue.'
        );
        return false;
      }
      return true;
    } catch (error) {
      console.error('JD validation error:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 400 && detail) {
        setJdWarning(formatErrorMessage(detail));
        return false;
      }
      // Validation outage must not trap the student: /start re-checks server-side.
      return true;
    } finally {
      setValidatingJd(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Show disclaimer on first use (cleared after agree)
    if (!sessionStorage.getItem('ai_disclaimer_agreed')) {
      setPendingSubmitEvent(e);
      setShowAiDisclaimer(true);
      return;
    }

    if ((mode === 'interviewer' || mode === 'counsellor' || mode === 'scorer') && !hasResume) {
      showToast('No resume found. Please upload your resume in the Settings page first!', 'warning');
      return;
    }

    if (mode === 'interviewer') {
      if (!selectedJobRole.trim()) {
        showToast('Please enter the target job title before starting the interview.', 'error');
        return;
      }
      if (jdInputMode !== 'none') {
        setSubmitted(true);
        const jdIsValid = await validateJobDescription();
        setSubmitted(false);
        if (!jdIsValid) return;
      }
      setInterviewStep('hardware');
      return;
    }

    if (mode === 'tutor' && !formData.topic && !formData.resume) {
      showToast('Please enter a topic or upload a PDF for the AI Tutor.', 'error');
      return;
    }

    setSubmitted(true);
    try {
      let endpoint = '';
      let data = new FormData();

      if (mode === 'counsellor') {
        endpoint = '/agent-engine/counsellor/start';
      } else if (mode === 'tutor') {
        endpoint = '/agent-engine/tutor/start';
        data.append('topic', formData.topic);
        if (formData.resume) {
          data.append('file', formData.resume);
        }
      } else {
        endpoint = '/ai-agents/resume/score';
      }

      const response = await api.post(endpoint, data);
      const result = response.data;

      if (mode === 'scorer') {
        setScorerResult(result);
        setScorerLocked(true);
        setSubmitted(false);
        return;
      }

      setCurrentSessionId(result.id);
      setSessionMeta(result);
      if (result.beyAgentId) {
        setBeyAgentId(result.beyAgentId);
      }
      if (result.resolvedEmbedUrl) {
        setResolvedEmbedUrl(result.resolvedEmbedUrl);
      }
      setShowAvatar(true);

    } catch (error) {
      console.error('Error:', error);
      const status = error.response?.status;
      if (status === 409) {
        // Another tab/request is already evaluating this resume: no second AI run.
        showToast(error.response?.data?.detail || 'This resume is already being evaluated.', 'info');
        setTimeout(refreshScorerAssessment, 4000);
      } else {
        showToast(error.response?.data?.detail || 'There was an error connecting to the server.', 'error');
      }
    } finally {
      setSubmitted(false);
    }
  };

  const startLiveInterview = async () => {
    setInterviewStep('active');
    setSubmitted(true);
    try {
      let data = new FormData();
      data.append('job_role', selectedJobRole.trim());
      data.append('level', level);
      if (jdInputMode === 'text' && jdText.trim()) {
        data.append('job_description_text', jdText.trim());
      }
      if (jdInputMode === 'file' && jdFile) {
        data.append('job_description_file', jdFile);
      }
      // No project payload: relevance is decided server-side from the job title
      // and JD, so there is no manual selection to send or to lose.

      const response = await api.post('/sidebar-interviewer/start', data);
      const result = response.data;

      setCurrentSessionId(result.id);
      setSessionMeta(result);
      if (result.beyAgentId) {
        setBeyAgentId(result.beyAgentId);
      }
      if (result.resolvedEmbedUrl) {
        setResolvedEmbedUrl(result.resolvedEmbedUrl);
      }
      setShowAvatar(true);

    } catch (error) {
      console.error('Error:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 400 && typeof detail === 'string' && detail.toLowerCase().includes('mismatch')) {
        // Server-side re-check disagreed: stay on setup with the reason attached
        // to the JD field instead of a toast that disappears while they read it.
        setJdWarning(`${detail} Fix the job title or the job description, then start again.`);
        setInterviewStep('setup');
        return;
      }
      showToast(detail || 'There was an error connecting to the server.', 'error');
      setInterviewStep('setup');
    } finally {
      setSubmitted(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'resume' && files.length > 0) {
      setFormData({ ...formData, resume: files[0] });
    } else if (name === 'topic') {
      setFormData({ ...formData, topic: value });
    }
  };

  const withMetadata = (baseUrl) => {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}metadata=${encodeURIComponent(
      JSON.stringify({ externalId: currentSessionId })
    )}`;
  };

  const avatarUrl = resolvedEmbedUrl
    ? withMetadata(resolvedEmbedUrl)
    : beyEmbedUrl
      ? withMetadata(beyEmbedUrl)
      : beyAgentId
        ? withMetadata(`https://bey.chat/${beyAgentId}`)
        : '';

  if (isLoadingProfile) {
    if (!embedded) return <Loader />;
    return (
      <div className="dashboard-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <h2>Loading Profile...</h2>
      </div>
    );
  }

  // All agent sub-features disabled for this role: show the "not available"
  // screen instead of silently opening the AI Interviewer fallback.
  if (!anyAgentEnabled) {
    const empty = <FeatureUnavailable featureLabel="AI Agents" />;
    if (embedded) return empty;
    return <StudentLayout activeTab="ai-agents">{empty}</StudentLayout>;
  }

  // --- Main Dashboard Screen ---
  const usageTooltip = buildUsageTooltip();

  const content = (
    <div className="ai-agents-root">
      <div className="ai-page-wrapper">

        {/* TOP FULL-WIDTH HEADER BOX */}
        <div className="ai-top-header-box">
          <div className="ai-header-top-row" style={{ flexDirection: 'column', gap: '24px' }}>
            <div className="ai-header-left">
              <span className="ai-badge-pill">
                <Brain size={14} /> AI AGENTS & TOOLS
              </span>
              <h2>{mode === 'scorer' ? 'AI Resume Scorer' : mode === 'counsellor' ? 'AI Counsellor' : mode === 'interviewer' ? 'AI Interviewer' : 'AI Tutor'}</h2>
              <p>
                {mode === 'scorer'
                  ? 'Analyze your resume against industry benchmarks and get instant actionable improvements.'
                  : mode === 'counsellor'
                    ? 'Get guidance on academic pathways, career counseling, and university recommendations.'
                    : mode === 'interviewer'
                      ? 'Practice mock interviews with personalized AI feedback based on your resume.'
                      : 'Explore complex topics and get 1-on-1 tutoring assistance with custom notes.'}
              </p>
            </div>
            <div className="ai-header-right-actions">
              <div
                className="ai-usage-badge ai-usage-badge-hover"
                tabIndex={0}
                aria-label={`AI time used ${formatTime(aiUsage)} of ${Math.floor(aiLimit / 60)} minutes`}
              >
                AI Time Used: {formatTime(aiUsage)} / {Math.floor(aiLimit / 60)}m
                <div className="ai-usage-hint" role="tooltip">
                  <div className="ai-usage-hint-title">AI Time Usage</div>
                  <div className="ai-usage-hint-row">
                    <span>Allocated limit</span>
                    <span>{usageTooltip.limitMinutes}m</span>
                  </div>
                  <div className="ai-usage-hint-row">
                    <span>Time used</span>
                    <span>{formatTime(aiUsage)} ({usageTooltip.pctUsed}%)</span>
                  </div>
                  <div className="ai-usage-hint-row">
                    <span>Time remaining</span>
                    <span>{formatTime(usageTooltip.remainingSeconds)}</span>
                  </div>
                  <div className="ai-usage-hint-note">{usageTooltip.note}</div>
                </div>
              </div>
              {mode !== 'scorer' && (
                <button
                  className="ai-action-btn secondary"
                  onClick={() => {
                    setShowHistoryInitially(true);
                  }}
                  title="View all previously saved reports"
                  style={{ background: 'rgba(59, 130, 246, 0.15)', borderColor: 'rgba(59, 130, 246, 0.3)', color: '#60a5fa' }}
                >
                  <History size={16} /> <span>Previous Reports</span>
                </button>
              )}
              {mode !== 'tutor' && (
                <button
                  className="ai-action-btn primary"
                  onClick={() => {
                    showToast("Opening Profile to manage resume...", "info");
                    navigate('/student-dashboard', { state: { showSettings: true } });
                  }}
                  title="Manage saved resume"
                >
                  <UploadCloud size={16} /> <span>Manage Resume</span>
                </button>
              )}
            </div>
          </div>

          {mode === 'interviewer' && (
            <div className="ai-candidate-disclaimer-banner" style={{ marginTop: '16px' }}>
              <Info size={16} className="ai-candidate-disclaimer-icon" />
              <div className="ai-candidate-disclaimer-text">
                <strong className="ai-candidate-disclaimer-title">Candidate Disclaimer:</strong>{' '}
                Feedback, evaluations, and scores generated by the AI Interviewer are automated and intended solely for mock interview practice, skill benchmarking, and developmental self-improvement. AI can make mistakes — always check and verify important information. Insights represent advisory guidance and do not constitute official hiring decisions or employment guarantees.
              </div>
            </div>
          )}
        </div>

        {/* TWO-COLUMN BODY GRID */}
        <div className="ai-body-grid">
          {/* LEFT COLUMN CARD (340px) */}
          <div className="ai-sidebar-card">
            <div className="ai-sidebar-header">
              <h4>{mode === 'scorer' ? 'Resume Scorer Guide' : mode === 'counsellor' ? 'Counsellor Guide' : mode === 'interviewer' ? 'Interviewer Guide' : 'Tutor Guide'}</h4>
              <span className="mode-badge">{mode.toUpperCase()}</span>
            </div>

            <div className="ai-sidebar-content">
              <div className="ai-info-section">
                <h5>Key Capabilities</h5>
                <ul>
                  {mode === 'interviewer' && (
                    <>
                      <li>• Tailored questions from your resume</li>
                      <li>• Real-time mock interview simulation</li>
                      <li>• Immediate scoring & feedback</li>
                    </>
                  )}
                  {mode === 'scorer' && (
                    <>
                      <li>• Comprehensive ATS keyword check</li>
                      <li>• Section-by-section impact rating</li>
                      <li>• Actionable formatting & content tips</li>
                    </>
                  )}
                  {mode === 'counsellor' && (
                    <>
                      <li>• Personalized degree pathway mapping</li>
                      <li>• University selection advice</li>
                      <li>• Career transition & skill guidance</li>
                    </>
                  )}
                  {mode === 'tutor' && (
                    <>
                      <li>• Deep-dive explanations on any topic</li>
                      <li>• Upload PDF study notes for Q&A</li>
                      <li>• Interactive concept breakdowns</li>
                    </>
                  )}
                </ul>
              </div>

              <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', marginTop: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Lightbulb size={18} color="#eab308" />
                  <h5 style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-main)', fontWeight: '700' }}>Pro Tip</h5>
                </div>
                <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
                  {mode === 'scorer' && "The scorer checks your resume against standard ATS parsers. Make sure your resume is text-based and avoid complex multi-column layouts for the best score."}
                  {mode === 'counsellor' && "Be as specific as possible about your career interests and past experiences so the AI can provide perfectly tailored academic pathways."}
                  {mode === 'interviewer' && "Ensure your microphone and camera are ready. Speak clearly and don't hesitate to ask the AI to repeat a question if needed."}
                  {mode === 'tutor' && "For the absolute best experience, upload your specific class notes (PDF) so the AI can tutor you directly on your curriculum material!"}
                </p>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN CARD (MAIN INTERACTION WINDOW) */}
          <div className="ai-main-card">
            {mode === 'scorer' && scorerResult ? (
              <div className="ai-main-inner-scroll">
                <ResumeScorerResults
                  scorerResult={scorerResult}
                  setScorerResult={setScorerResult}
                  studentProfile={studentProfile}
                  locked={scorerLocked}
                />
              </div>
            ) : (
              <div className="ai-main-empty-placeholder">
                <div className="ai-start-card">
                  <div className="ai-start-card-header">
                    <h3>
                      {mode === 'interviewer' ? 'Start Interview' : mode === 'counsellor' ? 'Start Session' : mode === 'tutor' ? 'Learn a Topic' : 'Score My Resume'}
                    </h3>
                    <p>
                      {mode === 'tutor'
                        ? 'Enter the topic you want to learn or upload notes below.'
                        : 'We will use your saved resume to personalize this session.'}
                    </p>
                  </div>

                  {aiUsage >= aiLimit && mode !== 'scorer' ? (
                    <div className="ai-limit-reached-box">
                      You have reached your daily limit of {aiLimit} AI Agent sessions.<br /><br />Please try again tomorrow.
                    </div>
                  ) : mode === 'interviewer' && interviewStep === 'hardware' ? (
                    <HardwareCheck 
                      onComplete={startLiveInterview} 
                      onCancel={() => setInterviewStep('setup')} 
                    />
                  ) : (
                    <form onSubmit={handleSubmit} className="ai-start-form">
                      {mode === 'interviewer' && (
                        <div className="tutor-form-inputs" style={{ marginBottom: '1.5rem', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          
                          {/* Level Selector */}
                          <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', marginBottom: '8px', color: '#0f172a' }}>
                              <BarChart2 size={16} style={{ color: '#3b82f6' }} /> Difficulty Level
                            </label>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                              {['Basic', 'Intermediate', 'Hard'].map(lvl => (
                                <button
                                  key={lvl}
                                  type="button"
                                  onClick={() => setLevel(lvl)}
                                  style={{
                                    padding: '10px 6px',
                                    borderRadius: '8px',
                                    background: level === lvl ? '#3b82f6' : '#f1f5f9',
                                    color: level === lvl ? '#fff' : '#475569',
                                    border: `1px solid ${level === lvl ? '#2563eb' : '#cbd5e1'}`,
                                    cursor: 'pointer',
                                    fontWeight: '600',
                                    fontSize: '0.875rem',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  {lvl}{lvl === 'Intermediate' ? ' (Default)' : ''}
                                </button>
                              ))}
                            </div>
                            <div style={{ marginTop: '10px', fontSize: '0.825rem', color: '#475569', background: '#f8fafc', padding: '10px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', lineHeight: '1.45' }}>
                              <span><strong>{level}{level === 'Intermediate' ? ' (Default)' : ''}:</strong> {LEVEL_SUMMARIES[level]}</span>
                            </div>
                          </div>

                          <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', marginBottom: '8px', color: '#0f172a' }}>
                              <Briefcase size={16} style={{ color: '#3b82f6' }} /> Target Job Role <span style={{ color: '#ef4444' }}>*</span>
                            </label>
                            
                            <input
                              type="text"
                              list="suggested-job-roles"
                              placeholder="e.g. Software Engineer, Data Scientist, Product Manager"
                              value={selectedJobRole}
                              onChange={(e) => setSelectedJobRole(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '12px 16px',
                                borderRadius: '10px',
                                background: '#ffffff',
                                border: '1px solid #cbd5e1',
                                color: '#0f172a',
                                fontSize: '0.95rem',
                                fontWeight: '500',
                                outline: 'none',
                                boxSizing: 'border-box',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                                transition: 'all 0.2s ease'
                              }}
                              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                              onBlur={(e) => e.target.style.borderColor = '#cbd5e1'}
                            />

                            {jobRoles.length > 0 && (
                              <datalist id="suggested-job-roles">
                                {jobRoles.map((role) => (
                                  <option key={role} value={role} />
                                ))}
                              </datalist>
                            )}

                            <span style={{ fontSize: '0.8rem', color: '#64748b', marginTop: '8px', display: 'block' }}>
                              Enter the job title you want to be interviewed for. Questions are built from this title, the job description, your resume and the projects you choose below.
                            </span>
                          </div>

                          {/* Job Description: optional, checked against the title before the interview */}
                          <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', marginBottom: '8px', color: '#0f172a' }}>
                              <FileText size={16} style={{ color: '#3b82f6' }} /> Job Description
                              <span style={{ color: '#64748b', fontWeight: '500', fontSize: '0.8rem' }}>(optional)</span>
                            </label>

                            <div style={{ display: 'flex', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '4px', gap: '4px' }}>
                              {JD_MODES.map(({ key, label }) => (
                                <button
                                  key={key}
                                  type="button"
                                  onClick={() => { setJdInputMode(key); setJdWarning(''); if (key === 'none') resetJobDescription(); }}
                                  style={{
                                    flex: 1,
                                    padding: '9px 6px',
                                    borderRadius: '7px',
                                    background: jdInputMode === key ? '#3b82f6' : 'transparent',
                                    color: jdInputMode === key ? '#fff' : '#cbd5e1',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontWeight: jdInputMode === key ? '700' : '500',
                                    fontSize: '0.82rem',
                                    transition: 'all 0.2s'
                                  }}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>

                            {jdInputMode === 'text' && (
                              <div style={{ marginTop: '10px' }}>
                                <textarea
                                  value={jdText}
                                  maxLength={MAX_JD_CHARS}
                                  rows={5}
                                  onChange={(e) => { setJdText(e.target.value); if (jdWarning) setJdWarning(''); }}
                                  placeholder="Paste the job description here. Its responsibilities, required skills and qualifications drive the interview questions..."
                                  style={{
                                    width: '100%',
                                    padding: '12px 14px',
                                    borderRadius: '10px',
                                    background: '#ffffff',
                                    border: `1px solid ${jdWarning ? '#ef4444' : '#cbd5e1'}`,
                                    color: '#0f172a',
                                    fontSize: '0.875rem',
                                    lineHeight: '1.5',
                                    resize: 'vertical',
                                    boxSizing: 'border-box',
                                    outline: 'none'
                                  }}
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '4px', fontSize: '0.75rem', color: '#64748b' }}>
                                  <span>Checked against your job title before the interview starts.</span>
                                  <span>{jdText.length} / {MAX_JD_CHARS}</span>
                                </div>
                              </div>
                            )}

                            {jdInputMode === 'file' && (
                              <div
                                style={{ marginTop: '10px' }}
                                onDragOver={(e) => { e.preventDefault(); setJdDragActive(true); }}
                                onDragLeave={() => setJdDragActive(false)}
                                onDrop={(e) => { e.preventDefault(); setJdDragActive(false); applyJdFile(e.dataTransfer.files && e.dataTransfer.files[0]); }}
                              >
                                <input
                                  type="file"
                                  id="jd-file-input"
                                  accept=".pdf,.doc,.docx,.txt"
                                  className="hidden-file-input"
                                  onChange={(e) => applyJdFile(e.target.files && e.target.files[0])}
                                />
                                {jdFile ? (
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '10px 12px', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '10px' }}>
                                    <span style={{ color: '#0f172a', fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📄 {jdFileName}</span>
                                    <button
                                      type="button"
                                      onClick={() => { setJdFile(null); setJdFileName(''); }}
                                      style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.8rem' }}
                                    >
                                      <X size={14} /> Remove
                                    </button>
                                  </div>
                                ) : (
                                  <label
                                    htmlFor="jd-file-input"
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: '6px',
                                      padding: '18px',
                                      cursor: 'pointer',
                                      textAlign: 'center',
                                      border: `1px dashed ${jdDragActive ? '#3b82f6' : '#cbd5e1'}`,
                                      borderRadius: '10px',
                                      background: jdDragActive ? '#eff6ff' : '#f8fafc',
                                      color: '#475569',
                                      fontSize: '0.85rem'
                                    }}
                                  >
                                    <UploadCloud size={20} style={{ color: '#3b82f6' }} />
                                    <span>Drop the job description here or click to browse</span>
                                    <span style={{ fontSize: '0.75rem', color: '#64748b' }}>PDF, DOC, DOCX or TXT</span>
                                  </label>
                                )}
                              </div>
                            )}

                            {jdWarning ? (
                              <div role="alert" style={{ marginTop: '10px', display: 'flex', gap: '8px', padding: '10px 12px', borderRadius: '10px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '0.82rem', lineHeight: '1.45' }}>
                                <span aria-hidden="true">⚠</span>
                                <span>{jdWarning}</span>
                              </div>
                            ) : jdInputMode !== 'none' ? (
                              <div style={{ marginTop: '10px', fontSize: '0.78rem', color: '#64748b' }}>
                                ✓ This job description is checked against your job title when you press Start.
                              </div>
                            ) : null}
                          </div>

                          {/* GitHub / portfolio projects used for project questions */}
                          <div className="form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '600', marginBottom: '8px', color: '#0f172a' }}>
                              <Brain size={16} style={{ color: '#3b82f6' }} /> GitHub Projects
                            </label>

                            <div style={{ fontSize: '0.82rem', color: '#64748b', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '10px 12px', lineHeight: '1.5' }}>
                              {availableProjects.length > 0
                                ? `${availableProjects.length} GitHub project${availableProjects.length === 1 ? '' : 's'} found on your profile. The interviewer picks the ones relevant to this job automatically and asks about those only.`
                                : 'No GitHub projects found. Connect GitHub (or add a project) in your student profile to get project-specific questions; the interview still uses your job title, JD and resume.'}
                            </div>
                          </div>
                        </div>
                      )}

                      {mode === 'tutor' && (
                        <div className="tutor-form-inputs">
                          <div className="form-group">
                            <label>Topic to Learn</label>
                            <input
                              type="text"
                              name="topic"
                              list="topic-suggestions"
                              placeholder="e.g. Quantum Physics, Machine Learning"
                              className="input-field"
                              value={formData.topic}
                              onChange={handleChange}
                            />
                            <datalist id="topic-suggestions">
                              {SUGGESTED_TOPICS.map((t) => (
                                <option key={t} value={t} />
                              ))}
                            </datalist>
                          </div>
                          <div className="form-group">
                            <label>Or Upload Notes (PDF)</label>
                            {!formData.resume ? (
                              <div className="custom-upload-container">
                                <input
                                  type="file"
                                  id="notes-upload"
                                  name="resume"
                                  accept="application/pdf"
                                  className="hidden-file-input"
                                  onChange={handleChange}
                                />
                                <label htmlFor="notes-upload" className="upload-placeholder" style={{ margin: 0 }}>
                                  <span>📄 UPLOAD NOTES (PDF)</span>
                                </label>
                              </div>
                            ) : (
                              <div className="file-status">
                                <span className="file-name">📄 {formData.resume.name}</span>
                                <button
                                  type="button"
                                  className="change-file-btn"
                                  onClick={() => setFormData({ ...formData, resume: null })}
                                >
                                  Change
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <button type="submit" className="ai-start-submit-btn" disabled={submitted || validatingJd}>
                        {validatingJd ? 'Checking job description...' :
                          submitted ? '✅ Processing...' :
                            mode === 'scorer' ? 'Analyze My Resume' :
                              `Start ${mode === 'interviewer' ? 'Interview' : mode === 'tutor' ? 'Tutor' : 'Session'}`}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* FULL WIDTH PERFORMANCE REPORT DISPLAYED BELOW THE GRID */}
        {(mode === 'counsellor' || mode === 'interviewer' || mode === 'tutor') && (
          <div id="ai-interviewer-report" style={{ marginTop: 0, width: '100%' }}>
            <PerformanceReport
              reportData={latestReport}
              mode={mode}
              loading={loadingReport}
              onRefresh={() => fetchLatestReport(mode)}
              onSelectReport={(rep) => setLatestReport(rep)}
              showHistoryInitially={showHistoryInitially}
              onCloseHistory={() => setShowHistoryInitially(false)}
              onCloseReport={() => setLatestReport(null)}
              backButtonText="Back to AI Agents Page"
            />
          </div>
        )}

        <AvatarModal
          showAvatar={showAvatar}
          setShowAvatar={setShowAvatar}
          mode={mode}
          avatarUrl={avatarUrl}
          setFormData={setFormData}
          setSubmitted={setSubmitted}
          currentSessionId={currentSessionId}
          sessionMeta={sessionMeta}
          onSessionEnded={handleSessionEnded}
        />

        {toast && (
          <div style={{
            position: 'fixed', top: '80px', right: '24px', zIndex: 9999,
            background: toast.type === 'error' ? 'rgba(255, 79, 107, 0.12)' : 'rgba(34, 217, 143, 0.12)',
            color: toast.type === 'error' ? '#ff4f6b' : '#22d98f',
            border: `1px solid ${toast.type === 'error' ? 'rgba(255, 79, 107, 0.2)' : 'rgba(34, 217, 143, 0.2)'}`,
            padding: '12px 20px', borderRadius: '8px', fontWeight: 500,
            boxShadow: '0 4px 6px rgba(0,0,0,0.4)',
            backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            {toast.message}
          </div>
        )}

        {/* Optionally display Chatbot globally if needed, or disable if avatars handle everything */}
        <Chatbot hidden={showAvatar} />

        {/* AI Disclaimer Modal */}
        {showAiDisclaimer && (
          <AiDisclaimerCard
            featureName={
              mode === 'scorer' ? 'AI Resume Scorer'
              : mode === 'counsellor' ? 'AI Counsellor'
              : mode === 'interviewer' ? 'AI Interviewer'
              : 'AI Tutor'
            }
            onAgree={() => {
              sessionStorage.setItem('ai_disclaimer_agreed', '1');
              setShowAiDisclaimer(false);
              if (pendingSubmitEvent) {
                handleSubmit(pendingSubmitEvent);
                setPendingSubmitEvent(null);
              }
            }}
            onCancel={() => {
              setShowAiDisclaimer(false);
              setPendingSubmitEvent(null);
            }}
          />
        )}
      </div>
    </div>
  );

  if (embedded) return content;
  return <StudentLayout activeTab="ai-agents">{content}</StudentLayout>;
}

export default AiAgents;
